import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Checkbox, Dropdown, FormRow, Label, QueryDropdown, TextField, WithTooltip } from 'basic-components';
import { Info } from 'basic-components/icons';
import { AuthEnums } from 'dashboard-services/api/auth';
import { DataLakeReadableConsts } from 'dashboard-services/api/dataLake';
import Query from 'dashboard-services/api/Query';

import getApiConfig from 'api/ApiConfig';
import { roleAuthQueries } from 'queries/modules/auth';

import { PipelinePicker } from 'primary/pickers';

import groupConfigService from '../../groupConfigService';
import useApprovalHandlersModule from '../../hooks/useApprovalHandlers';
import TemplateDropdown from './TemplateDropdown';

import { When } from 'react-if';

import './ApprovalSection.scss';

const EMPTY_ARRAY = []
const EXCLUDED_ROLE_CATEGORIES = [AuthEnums.ROLE_CATEGORY.INSIGHT_USER, AuthEnums.ROLE_CATEGORY.DEFAULT, AuthEnums.ROLE_CATEGORY.EXCEL, AuthEnums.ROLE_CATEGORY.INSIGHT]
const APPROVAL_ACTIONS = [
  { name: "update", label: "Update" },
  { name: "upload", label: "Upload" },
  { name: "delete", label: "Delete" }
]
const AFFECTED_FILE_TYPES_TOOLTIP = "Approval is required only for files of the selected types."
const ACTIONS_TOOLTIP = "At least one approval from the approvers will be required"

const selectRoles = data => (data?.pages || []).flatMap(page => page?.items || []).map(item => ({ value: item.egId, label: item.name }))

const useQueryBuilderNoFetching = () => {
  const dispatch = useDispatch()
  return useCallback(() => new Query.Builder(dispatch(getApiConfig())).withFetching(false), [dispatch])
}

const useReviewers = () => {
  const getQueryBuilder = useQueryBuilderNoFetching(),
        [query, setQuery] = useState(""),
        { data = EMPTY_ARRAY, isFetching, isFetchingNextPage, fetchNextPage } = roleAuthQueries.useSearchRoles({ getQueryBuilder, query, shouldRefetch: false, excludedCategories: EXCLUDED_ROLE_CATEGORIES, size: 30, select: selectRoles }),
        toReturn = useMemo(() => ({ options: data, isFetching: isFetching || isFetchingNextPage, fetchNextPage, setQuery }), [data, fetchNextPage, isFetching, isFetchingNextPage])
  return toReturn
}

// Stored draft configs keep only role ids, so names are loaded for reviewers that have none
const useReviewerOptions = ({ reviewers }) => {
  const getQueryBuilder = useQueryBuilderNoFetching(),
        ids = useMemo(() => reviewers.filter(({ name }) => !name).map(({ egId }) => egId), [reviewers]),
        { data: loadedRoles = EMPTY_ARRAY } = roleAuthQueries.useGetRoles({ getQueryBuilder, ids }),
        toReturn = useMemo(() => reviewers.map(({ egId, name }) => ({
          value: egId,
          label: name || loadedRoles.find(role => String(role.egId) === String(egId))?.name || String(egId)
        })), [loadedRoles, reviewers])
  return toReturn
}

const { useApprovalHandlers } = useApprovalHandlersModule

const hasError = ({ errors, field }) => errors.some(error => error.field === field)

const ApprovalSection = ({ approval, errors, menuPortalTarget = undefined, setGroupConfig }) => {
  const { fileTypes = EMPTY_ARRAY, actions, reviewers = EMPTY_ARRAY, useCustomNotification, pipeline, templates, reviewTtlDays } = approval,
        reviewersOptions = useReviewers(),
        defaultFileTypes = useMemo(() => fileTypes.map(fileType => DataLakeReadableConsts.FILE_TYPES.find(option => option.value === fileType) || { value: fileType, label: fileType }), [fileTypes]),
        defaultReviewers = useReviewerOptions({ reviewers }),
        defaultPipeline = useMemo(() => pipeline?.name ? { name: pipeline.name, groupName: pipeline.groupName } : undefined, [pipeline?.name, pipeline?.groupName]),
        { onChangeApproval, onChangeApprovalAction, onChangeApprovalTemplate, onChangeReviewTTLDays } = useApprovalHandlers({ setGroupConfig }),
        onChangeFileTypes = useCallback(value => onChangeApproval(value || EMPTY_ARRAY, { name: "fileTypes" }), [onChangeApproval]),
        onChangeReviewers = useCallback((_, { data }) => onChangeApproval((data || EMPTY_ARRAY).map(({ value, label }) => ({ egId: value, name: label })), { name: "reviewers" }), [onChangeApproval]),
        onChangePipeline = useCallback(value => onChangeApproval(value?.name ? { groupName: value.groupName, name: value.name } : null, { name: "pipeline" }), [onChangeApproval]),
        fileTypesRef = useRef(),
        reviewersRef = useRef(),
        shouldShowActionsError = hasError({ errors, field: "approvalActions" }) && !Object.values(actions || {}).some(Boolean)

  useEffect(() => {
    hasError({ errors, field: "approvalFileTypes" }) && fileTypesRef.current?.validateForForm()
    hasError({ errors, field: "approvalReviewers" }) && reviewersRef.current?.validateForForm()
  }, [errors])

  return (
    <div className="data-lake__group-dialog__advanced__approval-section">
      <FormRow isVertical>
        <Label required>
          <div className="data-lake__group-dialog__label-with-info">
            Affected file types
            <WithTooltip text={AFFECTED_FILE_TYPES_TOOLTIP}>
              <div className="data-lake__group-dialog__label-with-info__tooltip">
                <Info
                    color="blue-bright"
                    height={16}
                    width={16}
                />
              </div>
            </WithTooltip>
          </div>
        </Label>
        <Dropdown
            defaultValue={defaultFileTypes}
            menuPortalTarget={menuPortalTarget}
            multi
            name="fileTypes"
            onChange={onChangeFileTypes}
            options={DataLakeReadableConsts.FILE_TYPES}
            placeholder="Select file types"
            ref={fileTypesRef}
            required
            variant="border"
        />
      </FormRow>
      <FormRow isVertical>
        <Label required>
          <div className="data-lake__group-dialog__label-with-info">
            Auto close review after (days)
            <WithTooltip text="Number of days after the review will be automatically closed.">
              <div className="data-lake__group-dialog__label-with-info__tooltip">
                <Info
                    color="blue-bright"
                    height={16}
                    width={16}
                />
              </div>
            </WithTooltip>
          </div>
        </Label>
        <TextField
            defaultValue={reviewTtlDays}
            max={365}
            min={1}
            name="reviewTtlDays"
            onChange={onChangeReviewTTLDays}
            placeholder="Defaults to 30 days"
            required
            type="number"
            variant="border"
        />
      </FormRow>
      <FormRow isVertical>
        <Label
            noError
            required
        >
          <div className="data-lake__group-dialog__label-with-info">
            Actions that require approval
            <WithTooltip text={ACTIONS_TOOLTIP}>
              <div className="data-lake__group-dialog__label-with-info__tooltip">
                <Info
                    color="blue-bright"
                    height={16}
                    width={16}
                />
              </div>
            </WithTooltip>
          </div>
        </Label>
        <div className="data-lake__group-dialog__advanced__approval-section__actions">
          {APPROVAL_ACTIONS.map(({ name, label }) => (
            <Checkbox
                checked={!!actions?.[name]}
                key={name}
                name={name}
                onChange={onChangeApprovalAction}
            >
              {label}
            </Checkbox>
          ))}
        </div>
        <When condition={shouldShowActionsError}>
          <div className="data-lake__group-dialog__advanced__approval-section__actions-error">
            {errors.find(error => error.field === "approvalActions")?.message}
          </div>
        </When>
      </FormRow>
      <FormRow isVertical>
        <Label required>Reviewers</Label>
        <QueryDropdown
            defaultValue={defaultReviewers}
            fetchNextPage={reviewersOptions.fetchNextPage}
            isFetching={reviewersOptions.isFetching}
            menuPortalTarget={menuPortalTarget}
            multi
            name="reviewers"
            onChange={onChangeReviewers}
            onInputChange={reviewersOptions.setQuery}
            options={reviewersOptions.options}
            placeholder="Select reviewer"
            ref={reviewersRef}
            required
            variant="border"
        />
      </FormRow>
      <FormRow className="data-lake__group-dialog__advanced__approval-section__templates-checkbox">
        <Checkbox
            checked={!!useCustomNotification}
            name="useCustomNotification"
            onChange={onChangeApproval}
        >
          Use custom notification
        </Checkbox>
      </FormRow>
      <When condition={!!useCustomNotification}>
        {() => (
          <Fragment>
            <FormRow
                className="data-lake__group-dialog__advanced__approval-section__notification-template"
                isVertical
            >
              <Label>Notification pipeline</Label>
              <PipelinePicker
                  defaultValue={defaultPipeline}
                  name="pipeline"
                  onChange={onChangePipeline}
              />
            </FormRow>
            {groupConfigService.APPROVAL_TEMPLATE_KEYS.map(({ key, label }) => (
              <TemplateDropdown
                  key={key}
                  label={label}
                  menuPortalTarget={menuPortalTarget}
                  name={key}
                  onChange={onChangeApprovalTemplate}
                  template={templates?.[key] || undefined}
              />
            ))}
          </Fragment>
        )}
      </When>
    </div>
  )
}

ApprovalSection.propTypes = {
  approval: PropTypes.object.isRequired,
  errors: PropTypes.array.isRequired,
  menuPortalTarget: PropTypes.object,
  setGroupConfig: PropTypes.func.isRequired
}

export default memo(ApprovalSection)
